// Lazily-created Laravel Echo client (Reverb / pusher protocol) for the
// community 1-to-1 chat. Members authenticate to private channels with their
// encrypted member token via the custom /membership-chat-auth endpoint rather
// than a Laravel session, so we provide a custom authorizer.
import Echo from "laravel-echo";
import Pusher from "pusher-js";
import axios from "axios";
import config from "./config";

window.Pusher = Pusher;

let echoInstance = null;
let echoKey = null;

// Reverb client settings. Explicit REACT_APP_REVERB_* always win (the app key is
// a public client key, safe to ship). Otherwise derive per-environment the same
// way config.js does: local dev talks to localhost:8080 over ws, but a DEPLOYED
// build must NOT fall back to localhost — that produced an endless failing
// wss://localhost:8080 retry loop in production. When unset, point at the
// community-API host over wss/443 (Reverb is proxied on the API domain).
const IS_LOCALHOST = /^(localhost|127\.0\.0\.1)$/.test(
  typeof window !== "undefined" ? window.location.hostname : "localhost"
);
const API_HOST = (() => {
  try { return new URL(config.COMMUNITY_API_URL).hostname; } catch (e) { return "localhost"; }
})();
const REVERB_KEY = process.env.REACT_APP_REVERB_KEY || "5os5n9thfqxwyxczaiev";
const REVERB_HOST = process.env.REACT_APP_REVERB_HOST || (IS_LOCALHOST ? "localhost" : API_HOST);
const REVERB_PORT = Number(process.env.REACT_APP_REVERB_PORT || (IS_LOCALHOST ? 8080 : 443));
const REVERB_SCHEME = process.env.REACT_APP_REVERB_SCHEME || (IS_LOCALHOST ? "http" : "https");

/**
 * Get (or build) the shared Echo instance for the given member. Rebuilt if the
 * community/member identity changes. Returns null if identity is missing.
 */
export function getEcho({ communityToken, memberToken }) {
  if (!communityToken || !memberToken) return null;

  const key = `${communityToken}:${memberToken}`;
  if (echoInstance && echoKey === key) return echoInstance;
  if (echoInstance) {
    try {
      echoInstance.disconnect();
    } catch (e) {
      /* ignore */
    }
    echoInstance = null;
  }

  echoInstance = new Echo({
    broadcaster: "reverb",
    key: REVERB_KEY,
    wsHost: REVERB_HOST,
    wsPort: REVERB_PORT,
    wssPort: REVERB_PORT,
    forceTLS: REVERB_SCHEME === "https",
    enabledTransports: ["ws", "wss"],
    authorizer: (channel) => ({
      authorize: (socketId, callback) => {
        axios
          .post(`${config.COMMUNITY_API_URL}/membership-chat-auth`, {
            community_token: communityToken,
            member_token: memberToken,
            socket_id: socketId,
            channel_name: channel.name,
          })
          .then((res) => callback(null, res.data))
          .catch((err) => callback(err, null));
      },
    }),
  });
  echoKey = key;
  return echoInstance;
}

export function teardownEcho() {
  if (echoInstance) {
    try {
      echoInstance.disconnect();
    } catch (e) {
      /* ignore */
    }
  }
  echoInstance = null;
  echoKey = null;
}
